import { GetStaticPaths } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { NextPageWithLayout } from '@/layouts'
import { ExpressInterestLayout } from '@/layouts/ExpressInterestLayout'
import { ProjectByPhaseProvider } from '@/services/ProjectsService/ProjectProviders'
import { getProjectsInCurrentPhase } from '@/services/ProjectsService/ProjectsService'
import { getTorchContent, Torch } from '@/services/TorchesService'
import { GET_TORCH_BY_CAMPAIGN_SLUG } from '@/services/TorchesService/queries'
import { Get_Torch_By_Campaign_SlugQuery, Project } from '@/types/codegen-federation'
import { IPageCampaign, ISeoFields } from '@/types/generated-contentful'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { redact } from '@/utils/sensitive-data'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import { TorchView, TorchViewProps } from '@/views/TorchView'

interface TorchPageProps extends TorchViewProps {
  locale: string
  seo: ISeoFields
  project: Project
}

const TorchPage: NextPageWithLayout<TorchPageProps> = ({ locale, seo, project, ...props }) => {
  const { query } = useSafeRouter()
  const basePath = getBasePath(locale)
  const url = `${basePath}${paths.torches.index}/${query.torch}`
  return (
    <>
      <Seo
        title={seo.title}
        description={String(seo.description)}
        canonical={url}
        openGraph={{
          title: seo.title,
          description: seo.description,
          images: [
            ...(seo.image?.fields.file.url
              ? [
                  {
                    url: seo.image.fields.file.url,
                    width: 1200,
                    height: 630,
                    alt: seo.image.fields.title,
                  },
                ]
              : []),
          ],
          url,
        }}
        path={`${paths.torches.index}/${query.torch}`}
      />
      <ProjectByPhaseProvider project={project}>
        <TorchView {...props} />
      </ProjectByPhaseProvider>
    </>
  )
}

TorchPage.getLayout = (page) => {
  return <ExpressInterestLayout isDarkMode>{page}</ExpressInterestLayout>
}

export default TorchPage

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/torches/[torch]/index.tsx' },
  async ({ federationClient, locale, next404, params }) => {
    const torchSlug = getItemOrFirstEntry(params?.torch)

    if (!torchSlug) {
      return next404(Durations.ONE_HOUR_IN_SECONDS)
    }

    if (torchSlug === 'guardians') {
      return {
        redirect: {
          destination: `${paths.signup.gabrielAndTheGuardians}`,
          permanent: false,
        },
      }
    }

    if (torchSlug === 'germanking') {
      return {
        redirect: {
          destination: `${paths.signup.germanKing}`,
          permanent: false,
        },
      }
    }

    let torch: Torch
    try {
      const result = await federationClient.query<Get_Torch_By_Campaign_SlugQuery>({
        query: GET_TORCH_BY_CAMPAIGN_SLUG,
        variables: {
          campaignSlug: torchSlug,
        },
        errorPolicy: 'all',
      })
      torch = result.data.torchByCampaignSlug as Torch
    } catch (error) {
      logger().error('Torch Query Error', { torchSlug }, error)

      if (error instanceof Error) {
        if (error.message.includes('Not Found') || error.message.includes('No Torch found')) {
          return next404(Durations.FIFTEEN_MINUTES_IN_SECONDS)
        }
      }
      throw error
    }

    const projectsExpressingInterest = await getProjectsInCurrentPhase(
      { phaseSlug: 'express_interest' },
      federationClient,
    )
    const project = projectsExpressingInterest.find((p) => p.activeInterestInitiative?.contentfulSlug === torchSlug)

    const contentResponse = (await getTorchContent({
      content_type: 'pageCampaign',
      'fields.slug': torchSlug,
    }).catch((err) => {
      logger().error('Error getting contentful content', { torchSlug }, err)
      throw err
    })) as { items: IPageCampaign[] }

    const campaignAssetsResponse = await getTorchContent({
      content_type: 'campaignAssets',
      'fields.slug': torchSlug,
    }).catch((err) => {
      logger().error('Error getting campaign assets', { torchSlug }, err)
      throw err
    })

    if (!campaignAssetsResponse || !contentResponse || !project || !torch?.enabled)
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)

    const seo = contentResponse?.items?.[0]?.fields?.seo?.fields || {}
    const content = contentResponse?.items?.[0] || null
    const campaignAssets = campaignAssetsResponse?.items?.[0] || null

    return {
      props: {
        campaignAssets,
        content,
        locale,
        project: redact(project, []),
        seo,
        torch,
        ...(await loadTranslations(locale, ['common', 'account', 'torches'])),
      },
      revalidate: Durations.FIVE_MINUTES_IN_SECONDS,
    }
  },
)
